import type { ConsumerState } from '../../state'
import type { AppleGatewayIssueCardRequest, AppleGatewaySaveOfferRequest, PresentedOffer, UserCohort, InterestDetails } from '../../state/appleGateway/apis'
import { PaymentType } from '@afterpay/types'
import { v4 as uuidv4 } from 'uuid'
import type { EnrichedOfferDetail } from './hooks'
import { isValidString, valueIsNotNullOrUndefined } from '@afterpay/utils'
import { formatDateWithTimezone, formatUtcToTimezoneOffset } from '@afterpaytouch/core'
import { Route } from '../../config/router'
import { app, api } from '../../env'

export const parseHasInstoreCard = (value: string | undefined): boolean => {
  return value === 'true'
}

export const getNextApplePaylaterRoute = (hasInstoreCard: boolean): Route => {
  return hasInstoreCard ? Route.CARD_EDUCATION : Route.APPLE_PAYLATER_INITIALISE
}

const APPLE_PAYLATER_TIMEZONE = 'America/Los_Angeles'

const buildSelectedOfferLink = (cohort: UserCohort, token: string): string => {
  if (cohort === 'CASHAPP') {
    return `${app.CASH_COHORT_OFFER_LINK_URL}?client_id=${api.CASH_API_CLIENT_ID}&reference_id=${token}`
  }
  return `${app.AP_COHORT_OFFER_LINK_URL}/${token}`
}

const toEndOfDayUTC = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day, 23, 59, 59))
}

const formatDateForAppleGateway = (dateString: string): string => {
  const date = toEndOfDayUTC(dateString)
  return formatDateWithTimezone(date, APPLE_PAYLATER_TIMEZONE)
}

const getTodayDateString = (): string => {
  const today = new Date()
  // en-CA locale returns YYYY-MM-DD format
  return today.toLocaleDateString('en-CA', { timeZone: APPLE_PAYLATER_TIMEZONE })
}

const isValidOffer = (offer: EnrichedOfferDetail): boolean => {
  const schedules = offer.paymentSchedules
  const hasValidSchedules =
    valueIsNotNullOrUndefined(schedules) &&
    schedules.length > 0 &&
    valueIsNotNullOrUndefined(schedules[0].installmentDueDate) &&
    valueIsNotNullOrUndefined(schedules[schedules.length - 1].installmentDueDate)
  return (
    (offer.paymentType === PaymentType.PBI || offer.paymentType === PaymentType.PCL) &&
    valueIsNotNullOrUndefined(offer.numberOfInstallments) &&
    valueIsNotNullOrUndefined(offer.monthlyAmount) &&
    valueIsNotNullOrUndefined(offer.totalAmount) &&
    valueIsNotNullOrUndefined(offer.amountDueToday) &&
    hasValidSchedules
  )
}

const mapOfferToPresentedOffer = (offer: EnrichedOfferDetail): PresentedOffer | null => {
  const firstPaymentDueDate = offer.paymentSchedules[0].installmentDueDate
  const lastPaymentSchedule = offer.paymentSchedules[offer.paymentSchedules.length - 1]

  if (!isValidString(firstPaymentDueDate)) {
    return null
  }

  const offerId = uuidv4()

  const interestDetails: InterestDetails | undefined =
    valueIsNotNullOrUndefined(offer.apr) && offer.apr !== '' && valueIsNotNullOrUndefined(offer.interestAmount)
      ? {
          aprInterest: {
            percentage: (parseFloat(offer.apr) / 100).toFixed(4),
            totalInterest: offer.interestAmount,
          },
        }
      : undefined

  return {
    offerId,
    numberOfInstallments: offer.numberOfInstallments,
    recurringInstallmentAmount: offer.monthlyAmount,
    offerType: offer.paymentType as PaymentType.PBI | PaymentType.PCL,
    period: offer.paymentType === PaymentType.PBI ? ('daily' as const) : ('monthly' as const),
    total: offer.totalAmount,
    interval: offer.paymentType === PaymentType.PBI ? 14 : 1,
    downPayment: {
      amount: offer.amountDueToday.amount,
      currency: offer.amountDueToday.currency,
      dueBy: formatDateForAppleGateway(parseFloat(offer.amountDueToday.amount) > 0 ? firstPaymentDueDate : getTodayDateString()),
    },
    firstInstallmentDueDate: formatDateForAppleGateway(firstPaymentDueDate),
    finalInstallmentAmount: {
      amount: lastPaymentSchedule.amount.amount,
      currency: lastPaymentSchedule.amount.currency,
    },
    ...(valueIsNotNullOrUndefined(interestDetails) && { interestDetails }),
  }
}

const isMatchingOffer = (offer: EnrichedOfferDetail, selectedOffer: EnrichedOfferDetail): boolean =>
  offer.paymentType === selectedOffer.paymentType &&
  offer.numberOfInstallments === selectedOffer.numberOfInstallments &&
  offer.totalAmount.amount === selectedOffer.totalAmount.amount &&
  offer.monthlyAmount.amount === selectedOffer.monthlyAmount.amount

/**
 * Builds the saveOffer parameters for the continueCheckout request
 *
 * @param token - Checkout token
 * @param consumer - Consumer state with user details
 * @param offers - Array of offer details from consumerLending.paymentOptions (mapped to OfferDetail), each with its own paymentSchedules
 * @param selectedOffer - The offer the user selected
 * @param userCohort - User cohort (AFTERPAY or CASHAPP)
 * @param expires - Checkout expiration time in UTC ISO8601 format
 * @returns AppleGatewaySaveOfferRequest or null if required data is missing
 */
export const buildSaveOfferParams = (
  token: string,
  consumer: ConsumerState,
  offers: EnrichedOfferDetail[] | undefined,
  selectedOffer: EnrichedOfferDetail | undefined,
  userCohort: UserCohort,
  expires?: string
): AppleGatewaySaveOfferRequest | null => {
  // Validate required data
  const uuid = consumer.uuid
  const mobile = consumer.mobile
  if (
    !isValidString(uuid) ||
    !isValidString(mobile) ||
    !valueIsNotNullOrUndefined(offers) ||
    offers.length === 0 ||
    !valueIsNotNullOrUndefined(selectedOffer)
  ) {
    return null
  }

  const validOffers = offers.filter(isValidOffer)
  const presentedOffers: PresentedOffer[] = validOffers.map(mapOfferToPresentedOffer).filter(valueIsNotNullOrUndefined)

  if (presentedOffers.length === 0) {
    return null
  }

  const selectedIndex = validOffers.findIndex((offer) => isMatchingOffer(offer, selectedOffer))
  const selectedOfferId = selectedIndex >= 0 ? presentedOffers[selectedIndex].offerId : presentedOffers[0].offerId

  return {
    token,
    userWasDeclined: false,
    userCohort,
    userId: uuid,
    phoneNumber: mobile,
    offerDetails: {
      presentedOffer: presentedOffers,
      selectedOfferId,
      selectedOfferLink: buildSelectedOfferLink(userCohort, token),
      ...(valueIsNotNullOrUndefined(expires) && { validUntil: formatUtcToTimezoneOffset(expires, APPLE_PAYLATER_TIMEZONE) }),
    },
  }
}

/**
 * Builds the issueCard parameters for the continueCheckout request
 *
 * @param token - Checkout token
 * @param consumer - Consumer state with user details
 * @returns AppleGatewayIssueCardRequest or null if required data is missing
 */
export const buildIssueCardParams = (token: string, consumer: ConsumerState): AppleGatewayIssueCardRequest | null => {
  // Validate required consumer data
  if (!isValidString(consumer.uuid)) {
    return null
  }

  return {
    token,
    consumer: {
      consumerUuid: consumer.uuid,
    },
  }
}
